/**
 * CoverageBadge — per-widget denominator-coverage and FAR/OT applicability
 * indicators surfaced in widget headers.
 *
 * Three modes:
 *   - 'match-rate': "Based on N% of awards" (speed-to-contract style; uses
 *     `awards_with_solicitation_link / award_count` from the API response).
 *   - 'far-only-mixed': "FAR-only — N OT excluded" when a FAR-only metric
 *     is being shown over a slice that contains both FAR and OT awards.
 *   - 'far-only-all-ot': "n/a — FAR-only metric" when the slice is all-OT
 *     and the metric only meaningfully applies to FAR. Renderer should grey
 *     out the chart in this case (handled by the parent widget).
 *
 * Phase E2 of issue #90 v3 plan.
 */
import { AlertTriangle, Info } from 'lucide-react'

type Mode = 'match-rate' | 'far-only-mixed' | 'far-only-all-ot'

interface Props {
  mode: Mode
  /** For match-rate: awards_with_solicitation_link from API. */
  matchedCount?: number
  /** For match-rate: award_count from API (denominator). */
  totalCount?: number
  /** For far-only-mixed: number of OT awards excluded. */
  otCount?: number
}

export default function CoverageBadge({ mode, matchedCount, totalCount, otCount }: Props) {
  if (mode === 'match-rate') {
    if (totalCount === undefined || totalCount === 0) return null
    const pct = ((matchedCount ?? 0) / totalCount) * 100
    const tone = pct >= 50 ? 'normal' : 'warn'
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
          tone === 'warn'
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
        }`}
        title={`Speed-to-Contract is computed for awards matched to a SAM Opportunity. ${matchedCount?.toLocaleString()} of ${totalCount.toLocaleString()} awards matched.`}
      >
        <Info size={10} />
        Based on {pct.toFixed(0)}% of awards
      </span>
    )
  }

  if (mode === 'far-only-mixed') {
    if (otCount === undefined || otCount === 0) return null
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
        title={`This metric is computed over FAR awards only. ${otCount.toLocaleString()} OT awards in this slice are excluded from the denominator.`}
      >
        <AlertTriangle size={10} />
        FAR-only — {otCount.toLocaleString()} OT excluded
      </span>
    )
  }

  // 'far-only-all-ot'
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500"
      title="This metric only applies to FAR awards. The current slice is all-OT, so there is no FAR denominator to compute against."
    >
      <Info size={10} />
      n/a — FAR-only metric
    </span>
  )
}
